import * as THREE from "./hp-three.module.js";
import { GLTFLoader } from "./hp-gltf-loader.js";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const degToRad = (value) => (value * Math.PI) / 180;

const readCssPx = (name) => {
  const value = Number.parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue(name));
  return Number.isFinite(value) ? value : 0;
};

const setRewardsHeaderPx = (name, value) => {
  const cssValue = `${Math.max(0, value).toFixed(2)}px`;
  document.documentElement.style.setProperty(name, cssValue);
  document.body?.style.setProperty(name, cssValue);
};

const setRewardsHeaderSignedPx = (name, value) => {
  const cssValue = `${value.toFixed(2)}px`;
  document.documentElement.style.setProperty(name, cssValue);
  document.body?.style.setProperty(name, cssValue);
};

const isVisibleElement = (element) => {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 1 || rect.height <= 1) return false;

  const style = window.getComputedStyle(element);
  return style.display !== "none" && style.visibility !== "hidden" && style.opacity !== "0";
};

const findVisibleHeaderElement = (selectors, root = document) => (
  selectors
    .flatMap((selector) => Array.from(root.querySelectorAll(selector)))
    .find(isVisibleElement) || null
);

const measureRewardsHeaderBalance = () => {
  const header = document.querySelector(".site-header") || document.querySelector("#header-component");
  if (!header) return;

  const headerHeight = header.getBoundingClientRect().height || readCssPx("--header-height");
  const logoBottom = readCssPx("--hp-header-logo-visible-bottom") || headerHeight;
  const desktopNavLinks = Array.from(header.querySelectorAll(".desktop-nav-link, .desktop-header-inner nav a, nav a"));
  const shopLink = desktopNavLinks.find((link) => isVisibleElement(link) && /\bshop\b/i.test(link.textContent || ""));
  const navLink = shopLink || desktopNavLinks.find(isVisibleElement);
  const navBottom = navLink?.getBoundingClientRect().bottom || readCssPx("--top-row-height") || headerHeight;
  const menuStyle = document.querySelector("#header-component")?.dataset?.menuStyle || header.dataset?.menuStyle || "";
  const desktopNav = findVisibleHeaderElement([".desktop-header-inner nav", ".desktop-nav-list"], header);
  const mobileBar = findVisibleHeaderElement([".mobile-header-bar"], header);
  const isDrawerHeader = menuStyle === "drawer" || (!desktopNav && !!mobileBar);
  const balanceBottom = isDrawerHeader ? logoBottom : navBottom;

  setRewardsHeaderPx("--hp-rewards-header-logo-bottom", logoBottom);
  setRewardsHeaderPx("--hp-rewards-header-nav-bottom", navBottom);
  setRewardsHeaderPx("--hp-rewards-header-balance-bottom", balanceBottom);
  setRewardsHeaderSignedPx(
    "--hp-rewards-measured-header-boundary-shift",
    isDrawerHeader ? logoBottom * -0.25 : navBottom * 0.42,
  );
};

const measureRewardsVisibleCopyBalance = () => {
  const hero = document.querySelector(".hp-rewards-page--hero");
  if (!hero) return;

  if (window.innerWidth < 1025) {
    setRewardsHeaderSignedPx("--hp-rewards-measured-visible-copy-shift", 0);
    return;
  }

  const pill = hero.querySelector(".hp-rewards__pill");
  const button = hero.querySelector(".hp-btn");
  if (!isVisibleElement(pill) || !isVisibleElement(button)) return;

  const sectionRect = hero.getBoundingClientRect();
  const pageScroll = window.scrollY || document.documentElement.scrollTop || 0;
  if (pageScroll > 4 || sectionRect.top < -4) return;

  const pillRect = pill.getBoundingClientRect();
  const buttonRects = [
    button,
    hero.querySelector(".hp-btn__shadow"),
    hero.querySelector(".hp-btn__outline"),
    hero.querySelector(".hp-btn__content"),
  ]
    .filter(isVisibleElement)
    .map((element) => element.getBoundingClientRect());
  const buttonBottom = Math.max(...buttonRects.map((rect) => rect.bottom));
  const sectionBottom = Math.min(sectionRect.bottom || window.innerHeight, window.innerHeight);
  const boundaryBottom = readCssPx("--hp-rewards-header-balance-bottom");
  const heroStyle = window.getComputedStyle(hero);
  const currentShift = Number.parseFloat(heroStyle.getPropertyValue("--hp-rewards-visible-copy-shift")) || 0;
  const topGap = pillRect.top - boundaryBottom;
  const bottomGap = sectionBottom - buttonBottom;
  const nextShift = clamp(currentShift + (bottomGap - topGap) / 2, -180, 180);

  setRewardsHeaderSignedPx("--hp-rewards-measured-visible-copy-shift", nextShift);
};

let rewardsHeaderBalanceFrame = null;

const scheduleRewardsHeaderBalance = () => {
  cancelAnimationFrame(rewardsHeaderBalanceFrame);
  rewardsHeaderBalanceFrame = requestAnimationFrame(() => {
    rewardsHeaderBalanceFrame = null;
    measureRewardsHeaderBalance();
    requestAnimationFrame(() => {
      measureRewardsVisibleCopyBalance();
      requestAnimationFrame(measureRewardsVisibleCopyBalance);
    });
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", scheduleRewardsHeaderBalance, { once: true });
} else {
  scheduleRewardsHeaderBalance();
}

window.addEventListener("resize", scheduleRewardsHeaderBalance, { passive: true });
window.addEventListener("orientationchange", scheduleRewardsHeaderBalance, { passive: true });
window.addEventListener("load", scheduleRewardsHeaderBalance, { once: true });

class RewardsCardViewer extends HTMLElement {
  constructor() {
    super();
    this.frame = null;
    this.resizeObserver = null;
    this.pointerId = null;
    this.startX = 0;
    this.startY = 0;
    this.startRotationX = 0;
    this.startRotationY = 0;
    this.startPanX = 0;
    this.startPanY = 0;
    this.targetPanX = 0;
    this.targetPanY = 0;
    this.currentPanX = 0;
    this.currentPanY = 0;
    this.basePanX = 0;
    this.targetRotationX = -0.08;
    this.targetRotationY = -0.38;
    this.currentRotationX = this.targetRotationX;
    this.currentRotationY = this.targetRotationY;
    this.baseLift = 0;
    this.baseRotationZ = 0;
    this.defaultRotationX = 0;
    this.defaultRotationY = degToRad(-12);
    this.defaultRotationZ = 0;
    this.introStartTime = 0;
    this.introDuration = 1350;
    this.hoverDuration = 3600;
    this.hoverOffset = 0;
    this.shineDuration = 5600;
    this.shineGroup = null;
    this.shineMaterial = null;
    this.loadRequestId = 0;
    this.activeModelUrl = "";
    this.isCompactLayout = false;
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.layoutQuery = window.matchMedia("(max-width: 640px)");
    this.handleLayoutChange = () => this.loadModel();
  }

  connectedCallback() {
    this.setupScene();
    this.bindEvents();
    this.layoutQuery.addEventListener("change", this.handleLayoutChange);
    this.animate();
    this.loadModel();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
    this.layoutQuery.removeEventListener("change", this.handleLayoutChange);
    this.renderer?.dispose();
  }

  setupScene() {
    this.scene = new THREE.Scene();
    this.scene.background = null;

    this.camera = new THREE.PerspectiveCamera(34, 1, 0.01, 100);
    this.camera.position.set(0, 0, 5);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.12;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.append(this.renderer.domElement);

    this.modelRoot = new THREE.Group();
    this.scene.add(this.modelRoot);

    this.scene.add(new THREE.HemisphereLight(0xf7f5ef, 0x263238, 2.15));

    const key = new THREE.DirectionalLight(0xffffff, 2.7);
    key.position.set(3, 4, 4);
    this.scene.add(key);

    const rim = new THREE.DirectionalLight(0xc5f03b, 1.2);
    rim.position.set(-3, 1.8, -2.5);
    this.scene.add(rim);

    const fill = new THREE.PointLight(0xff4fa3, 1.2, 10);
    fill.position.set(-2.4, -1.2, 2.4);
    this.scene.add(fill);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this);
    this.resize();
  }

  loadModel() {
    const compactLayout = this.layoutQuery.matches;
    const modelUrl = compactLayout && this.dataset.mobileModel ? this.dataset.mobileModel : this.dataset.model;
    if (!modelUrl) {
      this.showError("Missing model");
      return;
    }

    const resolvedModelUrl = new URL(modelUrl, document.baseURI);
    const pageVersion = new URLSearchParams(window.location.search).get("v");
    if (pageVersion) resolvedModelUrl.searchParams.set("hp_card_v", pageVersion);
    if (this.activeModelUrl === resolvedModelUrl.href && this.modelRoot.children.length) return;

    this.activeModelUrl = resolvedModelUrl.href;
    this.isCompactLayout = compactLayout;
    this.loadRequestId += 1;
    const requestId = this.loadRequestId;
    this.classList.remove("is-loaded", "is-error");
    this.modelRoot.clear();
    this.shineGroup = null;
    this.shineMaterial = null;

    new GLTFLoader().load(
      resolvedModelUrl.href,
      (gltf) => {
        if (requestId !== this.loadRequestId) return;
        this.prepareModel(gltf.scene);
        this.modelRoot.add(gltf.scene);
        if (this.shineGroup) this.modelRoot.add(this.shineGroup);
        this.introStartTime = this.reduceMotion ? 0 : performance.now();
        this.classList.add("is-loaded");
      },
      undefined,
      () => this.showError("Card unavailable"),
    );
  }

  prepareModel(model) {
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.y, size.z) || 1;
    const isPortrait = size.y > size.x * 1.12;
    const compactScale = window.innerWidth >= 641 ? 3.28 : 3.62;
    const scale = (this.isCompactLayout ? compactScale : isPortrait ? 2.68 : 3.95) / maxDimension;
    const depthScale = 2.4;

    model.scale.set(scale, scale, scale * depthScale);
    model.position.set(-center.x * scale, -center.y * scale, -center.z * scale * depthScale);

    this.defaultRotationX = 0;
    this.defaultRotationY = degToRad(this.isCompactLayout ? -8 : -12);
    this.defaultRotationZ = 0;
    this.baseRotationZ = (this.isCompactLayout ? Math.PI / 2 + degToRad(-2) : 0) + this.defaultRotationZ;
    this.basePanX = this.isCompactLayout ? -0.08 : 0;
    this.targetRotationX = this.defaultRotationX;
    this.targetRotationY = this.defaultRotationY;
    this.currentRotationX = this.targetRotationX;
    this.currentRotationY = this.targetRotationY;
    this.baseLift = 0;

    model.traverse((child) => {
      if (!child.isMesh || !child.material) return;
      const texture = child.material.map || null;
      if (texture) texture.anisotropy = Math.min(this.renderer?.capabilities?.getMaxAnisotropy?.() || 1, 8);
      child.material = new THREE.MeshBasicMaterial({
        map: texture,
        color: texture ? 0xffffff : 0xf7f5ef,
        side: THREE.DoubleSide,
      });
    });

    this.createShine(size, scale, depthScale);
    this.resize();
  }

  createShine(size, scale, depthScale) {
    const shineOverscan = 1.035;
    const width = size.x * scale * shineOverscan;
    const height = size.y * scale * shineOverscan;
    const depth = size.z * scale * depthScale;
    const geometry = new THREE.PlaneGeometry(width, height);

    this.shineMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: THREE.FrontSide,
      uniforms: {
        uProgress: { value: -1 },
      },
      vertexShader: `
        varying vec2 vUv;

        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uProgress;
        varying vec2 vUv;

        float roundedMask(vec2 uv) {
          float radius = 0.06;
          vec2 q = abs(uv - 0.5) - vec2(0.5 - radius);
          float distance = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - radius;
          return 1.0 - smoothstep(0.0, 0.015, distance);
        }

        void main() {
          if (uProgress < 0.54) discard;

          float sweep = clamp((uProgress - 0.54) / 0.36, 0.0, 1.0);
          float diagonal = vUv.x * 1.18 + vUv.y * 0.68;
          float position = -0.42 + sweep * 2.08;
          float band = 1.0 - smoothstep(0.0, 0.17, abs(diagonal - position));
          float fade = smoothstep(0.0, 0.15, sweep) * (1.0 - smoothstep(0.82, 1.0, sweep));
          float alpha = band * fade * roundedMask(vUv) * 0.52;

          if (alpha < 0.01) discard;
          gl_FragColor = vec4(1.0, 1.0, 1.0, alpha);
        }
      `,
    });

    const front = new THREE.Mesh(geometry, this.shineMaterial);
    front.position.z = depth / 2 + 0.018;
    front.renderOrder = 10;

    const back = new THREE.Mesh(geometry, this.shineMaterial);
    back.position.z = -depth / 2 - 0.018;
    back.rotation.y = Math.PI;
    back.renderOrder = 10;

    this.shineGroup = new THREE.Group();
    this.shineGroup.add(front, back);
  }

  bindEvents() {
    this.addEventListener("pointerdown", (event) => this.startDrag(event));
    window.addEventListener("pointermove", (event) => this.moveDrag(event));
    window.addEventListener("pointerup", (event) => this.endDrag(event));
    window.addEventListener("pointercancel", (event) => this.endDrag(event));
  }

  startDrag(event) {
    if (event.pointerType === "mouse" && typeof event.button === "number" && event.button !== 0) return;

    this.pointerId = event.pointerId;
    this.introStartTime = 0;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startRotationX = this.targetRotationX;
    this.startRotationY = this.targetRotationY;
    this.startPanX = this.targetPanX;
    this.startPanY = this.targetPanY;
    this.classList.add("is-dragging");
    this.setPointerCapture?.(event.pointerId);
  }

  moveDrag(event) {
    if (event.pointerId !== this.pointerId) return;

    const deltaX = event.clientX - this.startX;
    const dragSpeed = this.isCompactLayout ? 1.12 : 0.62;
    const nextRotationY = this.startRotationY + degToRad(deltaX * dragSpeed);
    this.targetRotationX = this.defaultRotationX;
    this.targetRotationY = nextRotationY;
    this.targetPanX = 0;
    this.targetPanY = 0;
  }

  endDrag(event) {
    if (event.pointerId !== this.pointerId) return;

    this.pointerId = null;
    this.classList.remove("is-dragging");
    this.releasePointerCapture?.(event.pointerId);
    this.targetPanX = 0;
    this.targetPanY = 0;
  }

  resize() {
    if (!this.renderer || !this.camera) return;

    const { width, height } = this.getBoundingClientRect();
    const safeWidth = Math.max(1, Math.round(width));
    const safeHeight = Math.max(1, Math.round(height));
    this.renderer.setSize(safeWidth, safeHeight, false);
    const aspect = safeWidth / safeHeight;
    const wideSmallCanvas = !this.isCompactLayout && safeWidth < 520 && aspect >= 1.45;
    const roomyDesktopCanvas = !this.isCompactLayout && safeWidth >= 850;
    const midDesktopCanvas = !this.isCompactLayout && safeWidth >= 700;
    const narrowDesktopCanvas = !this.isCompactLayout && safeWidth >= 520 && safeWidth < 700;
    this.camera.aspect = aspect;
    this.camera.fov = this.isCompactLayout
      ? 39
      : safeWidth < 520 && !wideSmallCanvas
        ? 41
        : narrowDesktopCanvas
          ? 40
        : roomyDesktopCanvas
          ? 37.5
          : midDesktopCanvas
            ? 37.5
            : 38;
    this.camera.position.z = this.isCompactLayout
      ? 6.35
      : wideSmallCanvas
        ? 4.25
        : safeWidth < 520
          ? 5.4
          : narrowDesktopCanvas
            ? 5.85
          : roomyDesktopCanvas
            ? 5.25
            : midDesktopCanvas
              ? 4.95
              : 5;
    this.camera.updateProjectionMatrix();
  }

  animate() {
    this.frame = requestAnimationFrame(() => this.animate());

    const now = performance.now();
    const idleMotion = 0;
    let introSpin = 0;
    let introScale = 1;
    if (this.introStartTime) {
      const progress = clamp((now - this.introStartTime) / this.introDuration, 0, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      introSpin = (1 - eased) * -Math.PI * 6;
      introScale = 0.92 + eased * 0.08;
      if (progress >= 1) this.introStartTime = 0;
    }

    const targetHoverOffset =
      this.reduceMotion || this.pointerId !== null
        ? this.hoverOffset
        : Math.sin((now / this.hoverDuration) * Math.PI * 2) * 0.045;
    this.hoverOffset += (targetHoverOffset - this.hoverOffset) * 0.08;
    if (this.shineMaterial) {
      this.shineMaterial.uniforms.uProgress.value =
        this.reduceMotion || this.pointerId !== null ? -1 : (now % this.shineDuration) / this.shineDuration;
    }

    this.currentRotationX += (this.targetRotationX - this.currentRotationX) * 0.12;
    this.currentRotationY += (this.targetRotationY - this.currentRotationY) * 0.12;
    this.currentPanX += (this.targetPanX - this.currentPanX) * 0.12;
    this.currentPanY += (this.targetPanY - this.currentPanY) * 0.12;
    this.modelRoot.rotation.x = this.currentRotationX + idleMotion * 0.45;
    this.modelRoot.rotation.y = this.currentRotationY + idleMotion + introSpin;
    this.modelRoot.rotation.z = this.baseRotationZ + idleMotion * 0.18;
    this.modelRoot.scale.setScalar(introScale);
    this.modelRoot.position.x = this.basePanX + this.currentPanX;
    this.modelRoot.position.y = this.baseLift + this.currentPanY + this.hoverOffset;

    this.renderer.render(this.scene, this.camera);
  }

  showError(message) {
    const loader = this.querySelector(".hp-rewards-card-loader");
    if (loader) loader.textContent = message;
    this.classList.add("is-error");
  }
}

if (!customElements.get("hp-rewards-card-viewer")) {
  customElements.define("hp-rewards-card-viewer", RewardsCardViewer);
}
